import { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useLocale } from '@/hooks/useLocale'
import { Play, Pause, Volume2, ArrowRight, ChevronRight, ChevronLeft } from 'lucide-react'
import homePageApiService, { SectionCard } from '@/services/homePageApiService'
import Button from '@/components/ui/Button'
import { getLocalizedValue } from '@sakeena/shared'
import { useTranslation } from 'react-i18next'

// Helper function to normalize card data (handle both camelCase and PascalCase)
const normalizeCard = (card: any): SectionCard => ({
  id: card.id || card.Id || '',
  title: card.title || card.Title || '',
  body: card.body || card.Body || '',
  imageUrl: card.imageUrl || card.ImageUrl,
  audioUrl: card.audioUrl || card.AudioUrl,
  audioDurationSeconds: card.audioDurationSeconds ?? card.AudioDurationSeconds ?? null,
  ctaText: card.ctaText || card.CtaText || '',
  ctaLink: card.ctaLink || card.CtaLink || '',
  order: card.order ?? card.Order ?? 0,
  isActive: card.isActive ?? card.IsActive ?? true,
})

// Helper function to format duration
const formatDuration = (seconds?: number): string => {
  if (!seconds) return '0:00'
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

export default function SectionCardArticlePage() {
  const { t, i18n } = useTranslation()
  const currentLang = i18n.language || 'ar'
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { localizePath } = useLocale()
  const [card, setCard] = useState<SectionCard | null>(null)
  const [cards, setCards] = useState<SectionCard[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  useEffect(() => {
    const loadCard = async () => {
      if (!id) {
        setError('معرف المقال غير موجود')
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)
        
        // Load all cards to find previous/next
        const allCardsData = await homePageApiService.getActiveSectionCards()
        const normalizedCards = allCardsData.map(normalizeCard)
        const sortedCards = normalizedCards
          .filter(card => card.isActive)
          .sort((a, b) => (a.order || 0) - (b.order || 0))
        setCards(sortedCards)
        
        // Load current card
        const data = await homePageApiService.getSectionCardById(id)
        const normalized = normalizeCard(data)
        setCard(normalized)
      } catch (err) {
        console.error('Failed to load article:', err)
        setError('فشل تحميل المقال. يرجى المحاولة مرة أخرى.')
      } finally {
        setLoading(false)
      }
    }

    loadCard()
  }, [id, currentLang])

  useEffect(() => {
    // Cleanup audio on unmount
    return () => {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
      }
    }
  }, [])

  const handlePlayPause = () => {
    if (!card?.audioUrl) return

    if (!audioRef.current) {
      const audio = new Audio(card.audioUrl)
      audioRef.current = audio

      audio.addEventListener('timeupdate', () => {
        setCurrentTime(audio.currentTime)
      })

      audio.addEventListener('ended', () => {
        setIsPlaying(false)
        setCurrentTime(0)
      })
    }

    const audio = audioRef.current

    if (isPlaying) {
      audio.pause()
      setIsPlaying(false)
    } else {
      audio.play()
      setIsPlaying(true)
    }
  }

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen pt-20 pb-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-slate-600 py-20">جاري التحميل...</div>
        </div>
      </div>
    )
  }

  if (error || !card) {
    return (
      <div className="bg-gray-50 min-h-screen pt-20 pb-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-20">
            <p className="text-red-600 mb-4">{error || 'المقال غير موجود'}</p>
            <Button onClick={() => navigate(localizePath('/'))}>العودة للصفحة الرئيسية</Button>
          </div>
        </div>
      </div>
    )
  }

  const normalizedCard = normalizeCard(card)

  // Find previous and next cards
  const currentIndex = cards.findIndex(c => (c.id || '') === (normalizedCard.id || ''))
  const previousCard = currentIndex > 0 ? cards[currentIndex - 1] : null
  const nextCard = currentIndex < cards.length - 1 && currentIndex >= 0 ? cards[currentIndex + 1] : null

  const handlePrevious = () => {
    if (previousCard?.id) {
      navigate(localizePath(`/section-card/${previousCard.id}`))
    }
  }

  const handleNext = () => {
    if (nextCard?.id) {
      navigate(localizePath(`/section-card/${nextCard.id}`))
    }
  }

  return (
    <div className="bg-gray-50 min-h-screen pt-20 pb-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back button */}
        <button
          onClick={() => navigate(localizePath('/'))}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors"
        >
          <ArrowRight className="w-5 h-5 rotate-180" />
          <span>العودة للصفحة الرئيسية</span>
        </button>

        {/* Navigation and Article Card Container */}
        <div className="flex items-center gap-2 sm:gap-4 md:gap-6">
          {/* Previous Article Navigation - Right side (RTL) */}
          {previousCard ? (
            <button
              onClick={handlePrevious}
              className="flex flex-col items-center gap-1.5 sm:gap-2 group flex-shrink-0"
            >
              <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 flex items-center justify-center bg-white rounded-full shadow-lg hover:shadow-xl transition-all group-hover:scale-110 text-slate-700 group-hover:text-primary-600">
                <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7" />
              </div>
              <div className="text-center max-w-[100px] sm:max-w-[120px] md:max-w-[150px] hidden sm:block">
                <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 mb-0.5 md:mb-1">انتقال إلى</p>
                <p className="text-xs md:text-sm font-medium text-gray-800 dark:text-gray-200 line-clamp-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                  {getLocalizedValue(previousCard.title, currentLang)}
                </p>
              </div>
            </button>
          ) : (
            <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 flex-shrink-0" />
          )}

          {/* Article Card */}
          <div className="flex-1 bg-white rounded-[32px] shadow-[0_18px_60px_rgba(15,23,42,0.10)] overflow-hidden">
          {/* Image */}
          {normalizedCard.imageUrl && (
            <div className="w-full h-64 md:h-96 overflow-hidden">
              <img
                src={normalizedCard.imageUrl}
                alt={getLocalizedValue(normalizedCard.title, currentLang)}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <div className="p-6 md:p-8 lg:p-10">
            {/* Title */}
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 mb-6 leading-tight">
              {getLocalizedValue(normalizedCard.title, currentLang)}
            </h1>

            {/* Audio Player */}
            {normalizedCard.audioUrl && (
              <div className="mb-8 flex items-center gap-4 bg-emerald-50 rounded-2xl p-4">
                <button
                  onClick={handlePlayPause}
                  className="flex items-center justify-center w-12 h-12 rounded-full bg-slate-900 text-white shadow-lg hover:bg-slate-800 transition-colors flex-shrink-0"
                >
                  {isPlaying ? (
                    <Pause className="w-5 h-5" />
                  ) : (
                    <Play className="w-5 h-5 ml-0.5" />
                  )}
                </button>

                <div className="flex-1 flex items-center gap-3">
                  <div className="flex items-end gap-[2px] h-6 text-emerald-500 flex-1">
                    {[6, 10, 14, 9, 16, 12, 8, 13, 7, 11, 15, 10, 6].map((h, idx) => (
                      <span
                        key={idx}
                        className={`w-[2px] rounded-full transition-all ${
                          isPlaying ? 'bg-emerald-500 animate-pulse' : 'bg-emerald-300'
                        }`}
                        style={{ height: `${h}px` }}
                      />
                    ))}
                  </div>

                  <span className="text-sm font-medium text-slate-600 min-w-[3rem] text-left">
                    {formatDuration(normalizedCard.audioDurationSeconds)}
                  </span>

                  <button className="text-slate-500 hover:text-slate-700 transition-colors">
                    <Volume2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

            {/* Body Content (supports rich HTML from admin editor) */}
            <div
              className="prose prose-lg max-w-none text-slate-800 dark:text-slate-200 leading-loose text-lg md:text-xl font-medium space-y-4"
              dangerouslySetInnerHTML={{
                __html: getLocalizedValue(normalizedCard.body, currentLang) || '',
              }}
            />
          </div>
          </div>

          {/* Next Article Navigation - Left side (RTL) */}
          {nextCard ? (
            <button
              onClick={handleNext}
              className="flex flex-col items-center gap-1.5 sm:gap-2 group flex-shrink-0"
            >
              <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 flex items-center justify-center bg-white rounded-full shadow-lg hover:shadow-xl transition-all group-hover:scale-110 text-slate-700 group-hover:text-primary-600">
                <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7" />
              </div>
              <div className="text-center max-w-[100px] sm:max-w-[120px] md:max-w-[150px] hidden sm:block">
                <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 mb-0.5 md:mb-1">انتقال إلى</p>
                <p className="text-xs md:text-sm font-medium text-gray-800 dark:text-gray-200 line-clamp-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                  {getLocalizedValue(nextCard.title, currentLang)}
                </p>
              </div>
            </button>
          ) : (
            <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 flex-shrink-0" />
          )}
        </div>
      </div>
    </div>
  )
}






